import { photoPosts } from "./photoPosts";

export const posts = [
  {
    slug: "whatsapp-transcription-tools-compared",
    title: "WhatsApp voice notes and photos to text: tools compared",
    description: "Compare ways to turn WhatsApp voice notes and photos into text: Echo MSG, built-in transcripts, Live Text, Google Lens and desktop audio tools.",
    category: "Compare your options", topic: "both",
    minutes: 8,
    published: "2026-10-04",
    modified: "2026-10-04",
    answer: "For voice notes, compare WhatsApp’s built-in transcripts, Echo MSG and local audio tools. For photos and screenshots, compare Echo MSG, Apple Live Text and Google Lens. Echo MSG returns copyable text from either input in its WhatsApp chat; longer voice notes can also receive summaries. Choose by input, where you want the result and privacy needs.",
    sections: [
      ["comparison-method", "How we compared the options"],
      ["comparison-table", "Compare voice-note tools"],
      ["photo-comparison", "Compare photo-to-text tools"],
      ["photo-workflow", "Choose for photos and screenshots"],
      ["phone-or-desktop", "Choose for phone or computer"],
      ["copy-and-summarise", "Choose for copying or summaries"],
      ["processing-and-cost", "Check privacy and cost"],
      ["try-a-note", "Test a voice note and a photo"],
    ],
    faqs: [
      ["What is the best WhatsApp transcription tool?", "There is no single best choice for every workflow. WhatsApp’s own feature is a useful starting point for reading notes. Echo MSG returns copyable text in a separate chat. An on-device tool is a better fit when audio must stay on your device. This is an Echo MSG comparison, not an independent ranking or accuracy benchmark."],
      ["Can a WhatsApp bot transcribe every voice note automatically?", "Echo MSG starts processing after you send or forward a note to its chat. It does not monitor other conversations. Fully automatic processing of incoming business messages requires a separate integration and appropriate permissions."],
      ["Do I need to install an app to use Echo MSG?", "No separate Echo MSG app is required. Start its WhatsApp chat and forward a voice note or send a photo containing text. Your normal WhatsApp app or linked session remains the place where you receive the result."],
    ],
  },
  {
    slug: "how-to-transcribe-whatsapp-voice-notes",
    title: "How to transcribe WhatsApp voice notes",
    description: "Learn how to turn WhatsApp voice notes into text with built-in transcripts or Echo MSG. Compare copying, summaries, desktop use and privacy.",
    category: "Voice notes", topic: "voice",
    minutes: 7,
    published: "2026-10-04",
    modified: "2026-10-04",
    answer: "You can transcribe WhatsApp voice notes using WhatsApp’s built-in Voice Message Transcripts, where available, or by forwarding a note to Echo MSG. Echo MSG replies with copyable text in your WhatsApp chat and can add a summary and action point for longer notes.",
    sections: [
      ["choose", "Choose your transcription method"],
      ["whatsapp-transcripts", "Use WhatsApp’s built-in transcripts"],
      ["echo-msg", "Transcribe with Echo MSG"],
      ["desktop", "Use WhatsApp Web or desktop"],
      ["privacy", "Understand privacy and storage"],
      ["automatic", "What about automatic transcription?"],
      ["troubleshooting", "Get a more useful transcript"],
    ],
    faqs: [
      ["Can I turn WhatsApp voice notes into text for free?", "WhatsApp’s built-in transcription has no separate transcription subscription. Echo MSG offers free usage or a tryout, with limits and paid options shown in the chat. Your allowance can depend on how you joined; check the current offer before paying."],
      ["Do I need a WhatsApp Business account to use Echo MSG?", "No. You send or forward a voice note to Echo MSG from your existing WhatsApp account. You do not need to set up the WhatsApp Business API or install another app."],
      ["Does Echo MSG transcribe all my conversations automatically?", "No. Echo MSG processes messages you send or forward to its chat. It does not monitor your other conversations or place transcripts underneath the original message in another chat."],
      ["Which languages can I transcribe?", "WhatsApp’s available transcript languages depend on your device and software; check the list in its settings. Echo MSG’s transcription also depends on supported languages and audio quality. Try a short sample in your language or contact the team before relying on it for regular work."],
    ],
  },
  {
    slug: "copy-whatsapp-voice-note-transcripts",
    title: "How to copy a WhatsApp voice note transcript",
    description: "Need copyable text from a WhatsApp voice note? Forward it to Echo MSG, copy the text reply, and reuse it in an email, document or task list.",
    category: "Voice notes", topic: "voice",
    minutes: 4,
    published: "2026-10-04",
    modified: "2026-10-04",
    answer: "To get copyable text from a WhatsApp voice note, forward the audio to Echo MSG and copy the text message it sends back. That gives you a separate text reply you can paste into a document, email or task list.",
    sections: [
      ["built-in-copy", "Can you copy WhatsApp’s built-in transcript?"],
      ["copy-steps", "Get a copyable transcript with Echo MSG"],
      ["use-text", "Turn the text into something useful"],
      ["copy-privacy", "Check accuracy and sharing permissions"],
    ],
    faqs: [
      ["Where does the copyable transcript appear?", "It appears as a text reply in your conversation with Echo MSG. The original voice note stays in its original conversation."],
      ["Can I edit the transcript?", "Copy Echo MSG’s text reply into a document or a new message draft and edit your copy there. This does not change the original audio or the message Echo MSG sent."],
      ["Can I copy the transcript on a computer?", "Yes. Open the Echo MSG conversation in WhatsApp Web or your linked desktop app and use the available text selection or message copy controls. Exact controls vary by WhatsApp version."],
    ],
  },
  {
    slug: "summarize-whatsapp-voice-notes",
    title: "How to summarise WhatsApp voice notes with Echo MSG",
    description: "Turn long WhatsApp voice notes into a transcript, a short summary and a useful next step. See how Echo MSG handles spoken updates inside WhatsApp.",
    category: "Voice notes", topic: "voice",
    minutes: 5,
    published: "2026-10-04",
    modified: "2026-10-04",
    answer: "Forward a longer WhatsApp voice note to Echo MSG to receive its transcript and, when a summary is generated, a short version of the main points. Echo MSG can also highlight a clear action requested in the note. Results arrive in the Echo MSG chat.",
    sections: [
      ["transcript-summary", "A transcript, a summary and an action point"],
      ["summary-steps", "How to get a voice note summary"],
      ["example", "Example: a spoken work update"],
      ["team-workflow", "Use summaries in your team workflow"],
      ["summary-limits", "Know what a summary leaves out"],
    ],
    faqs: [
      ["Will every voice note get a summary?", "No. Summaries are intended for longer notes, and availability depends on successful processing. Short notes may only return a transcript."],
      ["Does Echo MSG create tasks or calendar events?", "Echo MSG can highlight a next step in its reply. You still need to copy it into your task manager or calendar; it is not an automatic task or calendar integration."],
      ["Does Echo MSG remember earlier messages?", "Echo MSG does not keep a stored conversation history for context-based replies. Treat each forwarded note as a separate message and keep any project context in your own approved tools."],
    ],
  },
  ...photoPosts,
];

export const postPath = (post) => `/blog/${post.slug}`;
